import { buildTimelineEventIso, type TimelinePrecisionOption } from './timeline-event-iso';
import {
  precisionIncludesTime,
  resolveTimelineTimeZone,
  utcIsoToZonedParts,
} from './timeline-timezone';

export type TimelineDetailLevel = TimelinePrecisionOption;

export type TimelineDetailLevelFields = {
  start_date: string;
  start_precision: string;
  start_timezone?: string | null;
  end_date: string | null;
  end_precision: string | null;
  end_timezone?: string | null;
};

function precisionRank(precision: string): number {
  if (precision === 'year') return 0;
  if (precision === 'month') return 1;
  if (precision === 'day') return 2;
  return 3;
}

function readCalendarParts(
  iso: string,
  precision: string,
  timeZone?: string | null
): { year: number; month: number; day: number } {
  if (precisionIncludesTime(precision)) {
    const parts = utcIsoToZonedParts(iso, resolveTimelineTimeZone(timeZone));
    return { year: parts.year, month: parts.month, day: parts.day };
  }
  const date = new Date(iso);
  return {
    year: date.getUTCFullYear(),
    month: date.getUTCMonth() + 1,
    day: date.getUTCDate(),
  };
}

function truncateSide(
  date: string,
  precision: string,
  timeZone: string | null | undefined,
  detailLevel: TimelineDetailLevel
): { date: string; precision: string; timezone: string | null } {
  if (precisionRank(precision) <= precisionRank(detailLevel)) {
    return {
      date,
      precision,
      timezone: precisionIncludesTime(precision) ? (timeZone ?? null) : null,
    };
  }
  const parts = readCalendarParts(date, precision, timeZone);
  return {
    date: buildTimelineEventIso(detailLevel, parts.year, parts.month, parts.day, '00:00', null),
    precision: detailLevel,
    timezone: null,
  };
}

export function applyTimelineDetailLevel<T extends TimelineDetailLevelFields>(
  item: T,
  detailLevel: TimelineDetailLevel | null | undefined
): T {
  if (!detailLevel) return item;

  const start = truncateSide(
    item.start_date,
    item.start_precision,
    item.start_timezone,
    detailLevel
  );

  if (!item.end_date || !item.end_precision) {
    if (start.date === item.start_date && start.precision === item.start_precision) {
      return item;
    }
    return {
      ...item,
      start_date: start.date,
      start_precision: start.precision,
      start_timezone: start.timezone,
    };
  }

  const end = truncateSide(item.end_date, item.end_precision, item.end_timezone, detailLevel);

  if (start.date === end.date) {
    return {
      ...item,
      start_date: start.date,
      start_precision: start.precision,
      start_timezone: start.timezone,
      end_date: null,
      end_precision: null,
      end_timezone: null,
    };
  }

  return {
    ...item,
    start_date: start.date,
    start_precision: start.precision,
    start_timezone: start.timezone,
    end_date: end.date,
    end_precision: end.precision,
    end_timezone: end.timezone,
  };
}

export function applyTimelineDetailLevelToItems<T extends TimelineDetailLevelFields>(
  items: T[],
  detailLevel: TimelineDetailLevel | null | undefined
): T[] {
  if (!detailLevel) return items;
  return items.map((item) => applyTimelineDetailLevel(item, detailLevel));
}
